import type { ReactNode } from "react";
import { Icon } from "@/components/icons";
import { Modal } from "@/components/modal";

type Props = {
  open: boolean;
  onClose: () => void;
  insetLeft?: number;
};

function FlowBox({
  tone,
  children,
}: {
  tone: "request" | "config" | "ignored" | "merged" | "note";
  children: ReactNode;
}) {
  const toneClasses: Record<typeof tone, string> = {
    request: "bg-grey-900 text-white",
    config: "bg-black text-white",
    ignored: "border border-dashed border-grey-300 text-grey-500",
    merged: "bg-green-500 text-white",
    note: "border border-grey-200 bg-white text-grey-900",
  };

  return (
    <div
      className={`text-pretty rounded-sm px-3 py-2 text-center text-small font-medium ${toneClasses[tone]}`}
    >
      {children}
    </div>
  );
}

function ArrowDown() {
  return <Icon name="arrow_downward" size={20} className="mx-auto text-grey-500" />;
}

export function AdverseMediaMergeInfoModal({ open, onClose, insetLeft }: Props) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Search + company settings: merge & override rules"
      description="How your company's adverse media defaults interact with a search request."
      width={800}
      insetLeft={insetLeft}
    >
      <div className="@container">
        <div className="grid grid-cols-1 gap-6 @lg:grid-cols-2">
          <div className="flex flex-col gap-2 rounded-2xl border border-dashed border-grey-300 p-4">
            <p className="text-small font-semibold text-green-700">MERGE — union of both</p>

            <div className="grid grid-cols-2 gap-2">
              <FlowBox tone="request">
                Search
                <br />
                Request
              </FlowBox>
              <FlowBox tone="config">Company Search Config</FlowBox>
            </div>
            <div className="grid grid-cols-2">
              <ArrowDown />
              <ArrowDown />
            </div>
            <FlowBox tone="merged">country · language · keywords — merged</FlowBox>
            <ArrowDown />
            <FlowBox tone="note">
              The search uses the combined values — company settings are never dropped, only added
              to.
            </FlowBox>
          </div>

          <div className="flex flex-col gap-2 rounded-2xl border border-dashed border-grey-300 p-4">
            <p className="text-small font-semibold text-grey-900">
              OVERRIDE — company setting always wins
            </p>

            <div className="grid grid-cols-2 gap-2">
              <FlowBox tone="ignored">Search Request (ignored)</FlowBox>
              <FlowBox tone="config">Company Search Config</FlowBox>
            </div>
            <div className="grid grid-cols-2">
              <span />
              <ArrowDown />
            </div>
            <FlowBox tone="note">
              min_score &amp; time_horizon always use the company setting, never the search request.
            </FlowBox>
          </div>
        </div>
      </div>
    </Modal>
  );
}
